import { useState, useRef, useEffect } from 'react';
import { FaClock, FaGlobe } from 'react-icons/fa';

interface DateTimePickerProps {
  onSelect: (timestamp: number) => void;
  onClose: () => void;
}

// Common timezones for selection
const TIMEZONES = [
  { name: 'Local Time', tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
  { name: 'UTC', tz: 'UTC' },
  { name: 'New York (EST/EDT)', tz: 'America/New_York' },
  { name: 'London (GMT/BST)', tz: 'Europe/London' },
  { name: 'Tokyo (JST)', tz: 'Asia/Tokyo' },
  { name: 'Sydney (AEST/AEDT)', tz: 'Australia/Sydney' },
  { name: 'Los Angeles (PST/PDT)', tz: 'America/Los_Angeles' },
  { name: 'Dubai (GST)', tz: 'Asia/Dubai' },
];

export const DateTimePicker = ({ onSelect, onClose }: DateTimePickerProps) => {
  const [date, setDate] = useState(() => {
    const now = new Date();
    return now.toISOString().slice(0, 10); // YYYY-MM-DD
  });
  const [time, setTime] = useState(() => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // HH:MM
  });
  const [selectedTimezone, setSelectedTimezone] = useState(TIMEZONES[0].tz);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleInsert = () => {
    // Parse the date and time input
    const [year, month, day] = date.split('-').map(Number);
    const [hours, minutes] = time.split(':').map(Number);

    // If using local timezone, create date directly
    if (selectedTimezone === Intl.DateTimeFormat().resolvedOptions().timeZone) {
      const localDate = new Date(year, month - 1, day, hours, minutes);
      const timestamp = Math.floor(localDate.getTime() / 1000);
      onSelect(timestamp);
      onClose();
      return;
    }

    // For other timezones, we need to interpret the input as being in that timezone
    // Create an ISO string that represents the date/time in UTC
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;

    // Create a reference date to calculate timezone offset
    const referenceUTC = new Date(`${dateStr}T${timeStr}Z`);

    // Format this UTC date in the target timezone to see what local time it shows
    const tzFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: selectedTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = tzFormatter.formatToParts(referenceUTC);
    const tzYear = parseInt(parts.find((p) => p.type === 'year')?.value || '0');
    const tzMonth = parseInt(parts.find((p) => p.type === 'month')?.value || '0');
    const tzDay = parseInt(parts.find((p) => p.type === 'day')?.value || '0');
    const tzHour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0');
    const tzMinute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0');

    // Calculate the difference
    const wantedTime = new Date(year, month - 1, day, hours, minutes);
    const gotTime = new Date(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute);
    const offset = wantedTime.getTime() - gotTime.getTime();

    // Apply the offset to get the correct UTC timestamp
    const correctUTC = new Date(referenceUTC.getTime() + offset);
    const timestamp = Math.floor(correctUTC.getTime() / 1000);

    onSelect(timestamp);
    onClose();
  };

  const setNow = () => {
    const now = new Date();

    // Set to current time in selected timezone
    if (selectedTimezone === Intl.DateTimeFormat().resolvedOptions().timeZone) {
      setDate(now.toISOString().slice(0, 10));
      setTime(now.toTimeString().slice(0, 5));
    } else {
      // Get current time in selected timezone
      const tzTime = new Date(now.toLocaleString('en-US', { timeZone: selectedTimezone }));
      setDate(tzTime.toISOString().slice(0, 10));
      setTime(tzTime.toTimeString().slice(0, 5));
    }
  };

  return (
    <div
      ref={pickerRef}
      className="absolute bottom-full mb-2 left-0 bg-brand-surface/95 dark:bg-[#1f1c48]/95 backdrop-blur-md border border-brand-outline-variant dark:border-brand-primary/30 rounded-xl shadow-2xl p-4 z-50 min-w-[280px] sm:min-w-[320px]"
    >
      <div className="flex items-center gap-2 mb-3">
        <FaClock className="text-primary-600" />
        <h3 className="font-semibold text-brand-on-background dark:text-brand-on-primary">
          Select Date & Time
        </h3>
      </div>

      <div className="space-y-3">
        <div>
          <label
            htmlFor="timezone-select"
            className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1"
          >
            <FaGlobe className="inline mr-1" />
            Timezone
          </label>
          <select
            id="timezone-select"
            value={selectedTimezone}
            onChange={(e) => setSelectedTimezone(e.target.value)}
            className="w-full px-3 py-2 border border-brand-outline-variant dark:border-brand-outline-variant rounded-lg bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary focus:ring-2 focus:ring-primary-500"
          >
            {TIMEZONES.map((tz, index) => (
              <option key={`${tz.tz}-${index}`} value={tz.tz}>
                {tz.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="date-input"
            className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1"
          >
            Date
          </label>
          <input
            id="date-input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 border border-brand-outline-variant dark:border-brand-outline-variant rounded-lg bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="time-input"
            className="block text-sm font-medium text-brand-on-surface dark:text-brand-on-surface-variant mb-1"
          >
            Time
          </label>
          <input
            id="time-input"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full px-3 py-2 border border-brand-outline-variant dark:border-brand-outline-variant rounded-lg bg-brand-surface dark:bg-brand-surface-variant text-brand-on-background dark:text-brand-on-primary focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="text-xs text-brand-outline dark:text-brand-on-surface-variant italic">
          {selectedTimezone === Intl.DateTimeFormat().resolvedOptions().timeZone
            ? 'Using your local timezone'
            : `Time will be interpreted as ${TIMEZONES.find((tz) => tz.tz === selectedTimezone)?.name}`}
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={setNow}
            className="flex-1 px-3 py-2 text-sm bg-brand-background dark:bg-brand-surface-variant text-brand-on-surface dark:text-brand-on-surface-variant rounded-lg hover:bg-brand-surface dark:hover:bg-brand-surface-variant transition-colors"
          >
            Now
          </button>
          <button
            type="button"
            onClick={handleInsert}
            className="flex-1 px-3 py-2 text-sm bg-primary-600 text-brand-on-primary rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
};
